import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { CrossClientQueryExecution, CrossClientQueryExecutionProgress, CrossClientQueryInitializedExecution } from '../../models/CrossClient'
import { User } from '../../models/User'
import { executionIsRunning } from './utils'

interface CrossClientLandingState {
  initLoading: boolean
  evaluateLoading: boolean
  newExecution: CrossClientQueryInitializedExecution | null
  runningExecutions: CrossClientQueryExecution[]
  runningExecutionProgresses: CrossClientQueryExecutionProgress[]
  runningExecutionsLoading: boolean
  finishedExecutions: CrossClientQueryExecution[]
  totalFinishedExecutions: number
  finishedExecutionProgresses: CrossClientQueryExecutionProgress[]
  finishedExecutionsLoading: boolean
  finishedExecutionsLoadingMore: boolean
  pollingEnabled: boolean
  teammates: User[]
  teammatesLoading: boolean
}

const initialState: CrossClientLandingState = {
  initLoading: false,
  evaluateLoading: false,
  newExecution: null,
  runningExecutions: [],
  runningExecutionProgresses: [],
  runningExecutionsLoading: true,
  finishedExecutions: [],
  totalFinishedExecutions: 0,
  finishedExecutionProgresses: [],
  finishedExecutionsLoading: true,
  finishedExecutionsLoadingMore: false,
  pollingEnabled: false,
  teammates: [],
  teammatesLoading: true,
}

export const crossClientLandingSlice = createSlice({
  name: 'crossClientLanding',
  initialState,
  reducers: {
    setCrossClientInitLoading: (state, action: PayloadAction<boolean>) => {
      state.initLoading = action.payload
    },
    setCrossClientEvaluateLoading: (state, action: PayloadAction<boolean>) => {
      state.evaluateLoading = action.payload
    },

    setCrossClientLandingExecution: (state, action: PayloadAction<CrossClientQueryInitializedExecution | null>) => {
      state.newExecution = action.payload
    },
    addCrossClientLandingFinishedExecutions: (state, action: PayloadAction<CrossClientQueryExecution[]>) => {
      const newExecutions = action.payload.filter(
        (e) =>
          !state.finishedExecutions.some((finishedExecution) => finishedExecution.id === e.id) &&
          !state.runningExecutions.some((runningExecution) => runningExecution.id === e.id),
      )
      state.finishedExecutions.push(...newExecutions)
    },
    stopCrossClientLandingExecution: (state, action: PayloadAction<CrossClientQueryExecution>) => {
      state.runningExecutions = state.runningExecutions.filter((e) => e.id !== action.payload.id)
      state.finishedExecutions.push(action.payload)
    },
    addCrossClientLandingFinishedExecutionProgresses: (state, action: PayloadAction<CrossClientQueryExecutionProgress[]>) => {
      state.finishedExecutionProgresses.push(...action.payload)
    },
    setCrossClientLandingFinishedExecutionsLoading: (state, action: PayloadAction<boolean>) => {
      state.finishedExecutionsLoading = action.payload
    },
    setCrossClientLandingFinishedExecutionsLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.finishedExecutionsLoadingMore = action.payload
    },
    setCrossClientLandingTotalFinishedExecutions: (state, action: PayloadAction<number>) => {
      state.totalFinishedExecutions = action.payload
    },
    setCrossClientLandingRunningExecutions: (state, action: PayloadAction<CrossClientQueryExecution[]>) => {
      state.runningExecutions = action.payload
    },
    setCrossClientLandingRunningExecutionProgresses: (state, action: PayloadAction<CrossClientQueryExecutionProgress[]>) => {
      state.runningExecutionProgresses = action.payload
      const finishedProgresses = action.payload.filter((e) => !executionIsRunning(e))
      for (const progress of finishedProgresses) {
        const runningExecution = state.runningExecutions.find((e) => e.id === progress.executionId)
        if (runningExecution) {
          runningExecution.phase = progress.phase
          runningExecution.updatedAt = progress.lastUpdatedAt
        }
      }
    },
    setCrossClientLandingRunningExecutionsLoading: (state, action: PayloadAction<boolean>) => {
      state.runningExecutionsLoading = action.payload
    },

    startCrossClientLandingPolling: (state) => {
      state.pollingEnabled = true
    },
    stopCrossClientLandingPolling: (state) => {
      state.pollingEnabled = false
    },
    setCrossClientLandingTeammates: (state, action: PayloadAction<User[]>) => {
      state.teammates = action.payload
    },
    setCrossClientLandingTeammatesLoading: (state, action: PayloadAction<boolean>) => {
      state.teammatesLoading = action.payload
    },
    resetCrossClientLanding: () => initialState,
  },
})

export const {
  setCrossClientInitLoading,
  setCrossClientEvaluateLoading,
  addCrossClientLandingFinishedExecutions,
  addCrossClientLandingFinishedExecutionProgresses,
  setCrossClientLandingExecution,
  startCrossClientLandingPolling,
  stopCrossClientLandingPolling,
  setCrossClientLandingRunningExecutions,
  setCrossClientLandingRunningExecutionProgresses,
  setCrossClientLandingRunningExecutionsLoading,
  setCrossClientLandingFinishedExecutionsLoading,
  setCrossClientLandingFinishedExecutionsLoadingMore,
  stopCrossClientLandingExecution,
  setCrossClientLandingTotalFinishedExecutions,
  setCrossClientLandingTeammates,
  setCrossClientLandingTeammatesLoading,
  resetCrossClientLanding,
} = crossClientLandingSlice.actions

export const selectCrossClientInitLoading = (state: RootState) => state.crossClientLanding.initLoading

export const selectCrossClientEvaluateLoading = (state: RootState) => state.crossClientLanding.evaluateLoading

export const selectCrossClientLandingNewExecution = (state: RootState) => state.crossClientLanding.newExecution

export const selectCrossClientLandingPollingEnabled = (state: RootState) => state.crossClientLanding.pollingEnabled

export const selectCrossClientLandingFinishedExecutions = (state: RootState) => state.crossClientLanding.finishedExecutions

export const selectCrossClientLandingFinishedExecutionProgresses = (state: RootState) => state.crossClientLanding.finishedExecutionProgresses

export const selectCrossClientLandingRunningExecutions = (state: RootState) => state.crossClientLanding.runningExecutions

export const selectCrossClientLandingRunningExecutionProgresses = (state: RootState) => state.crossClientLanding.runningExecutionProgresses

export const selectCrossClientLandingFinishedExecutionsLoading = (state: RootState) => state.crossClientLanding.finishedExecutionsLoading

export const selectCrossClientLandingFinishedExecutionsLoadingMore = (state: RootState) => state.crossClientLanding.finishedExecutionsLoadingMore

export const selectCrossClientLandingRunningExecutionsLoading = (state: RootState) => state.crossClientLanding.runningExecutionsLoading

export const selectCrossClientLandingTotalFinishedExecutions = (state: RootState) => state.crossClientLanding.totalFinishedExecutions

export const selectCrossClientLandingRunningExecutionCount = (state: RootState) => {
  return state.crossClientLanding.runningExecutions.filter(executionIsRunning).length
}

export const selectCrossClientLandingTeammates = (state: RootState) => state.crossClientLanding.teammates

export const selectCrossClientLandingTeammatesLoading = (state: RootState) => state.crossClientLanding.teammatesLoading

export default crossClientLandingSlice.reducer
